import { useMutation } from '@apollo/client';
import {
  ContextualMenu,
  IDragOptions,
  Modal,
  Separator,
  Stack,
  makeStyles,
} from '@fluentui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { showCustomToast } from 'common/components/CustomToast/utils';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  AccessGroupCreate,
  AccessGroupCreateVariables,
} from './__generated__/AccessGroupCreate';
import { BasicForm } from './BasicForm';
import { Footer } from './Footer';
import { GroupItems } from './GroupItems';
import { Header } from './Header';
import { getDefaultValues, getDeletedItems, getNewGroupItems } from './utils';
import { validationSchema } from './validations';

import {
  AccessGroupInput,
  AccessGroupPatch,
  AccessGroupUpdateInput,
} from 'common/types/globalTypes';
import { isEmpty } from 'lodash';
import { AccessGroupRow } from '../../list';
import ACCESS_GROUP_FIELDS from '../../list/AccessGroupsFields.graphql';
import {
  AccessGroupUpdate,
  AccessGroupUpdateVariables,
} from './__generated__/AccessGroupUpdate';
import ACCESS_GROUP_CREATE from './AccessGroupCreate.graphql';
import ACCESS_GROUP_UPDATE from './AccessGroupUpdate.graphql';
import { AccessGroupValues } from './types';
import { ACCESS_GROUP_INITIAL_VALUES } from './constants';
const ACCESS_GROUP_FRAGMENT_NAME = 'AccessGroupsFields';

const DragOptions: IDragOptions = {
  moveMenuItemText: 'Move',
  closeMenuItemText: 'Close',
  menu: ContextualMenu,
  dragHandleSelector: '.ms-Modal-scrollableContent > div > div:first-child ',
};

const useStyles = makeStyles(() => ({
  container: {
    width: 800,
    maxHeight: 800,
  },
}));

interface AccessGroupsViewModalProps {
  isOpen: boolean;
  accessGroup?: AccessGroupRow;
  onClose: () => void;
}

export const AccessGroupsViewModal: React.FC<AccessGroupsViewModalProps> = ({
  isOpen,
  accessGroup,
  onClose,
}) => {
  const styles = useStyles();
  const formMethods = useForm<AccessGroupValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });

  const [createAccessGroup, { loading: createLoading }] = useMutation<
    AccessGroupCreate,
    AccessGroupCreateVariables
  >(ACCESS_GROUP_CREATE, { errorPolicy: 'all' });

  const [updateAccessGroup, { loading: updateLoading }] = useMutation<
    AccessGroupUpdate,
    AccessGroupUpdateVariables
  >(ACCESS_GROUP_UPDATE, { errorPolicy: 'all' });

  const { handleSubmit, reset, trigger } = { ...formMethods };

  const { id, _rowTimestamp, _isUpdatable } = { ...accessGroup };

  const onHandleSubmit = async (values: AccessGroupValues) => {
    const { name, description, accessGroupItems } = { ...values };
    const accessGroupPayload: AccessGroupInput = {
      name: name!,
      description,
    };

    if (accessGroup) {
      const defaultValues = getDefaultValues({ accessGroup });
      const accessGroupPatch: AccessGroupPatch = Object.entries(values).reduce(
        (res, [key, val]) => {
          if (val !== defaultValues[key as keyof AccessGroupValues]) {
            if (key === 'accessGroupItems') return { ...res };
            else return { ...res, [key]: val };
          }
          return res;
        },
        {}
      );

      const newGroupItems = getNewGroupItems({
        existingAccessGroupItems: accessGroup?.accessGroupItems.nodes,
        currentAccessGroupItems: accessGroupItems || [],
      });
      const deletedGroupItems = getDeletedItems({
        existingAccessGroupItems: accessGroup?.accessGroupItems.nodes,
        currentAccessGroupItems: accessGroupItems || [],
      });

      const { errors } = await updateAccessGroup({
        variables: {
          input: {
            id,
            rowTimestamp: _rowTimestamp,
            accessGroupPatch: !isEmpty(accessGroupPatch)
              ? accessGroupPatch
              : undefined,
            accessGroupItemsCreate: newGroupItems,
            accessGroupItemsDelete: deletedGroupItems,
          } as AccessGroupUpdateInput,
        },
      });
      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        showCustomToast({
          content: 'User access pool edited successfully',
          appearance: 'success',
        });
      }
    } else {
      const { errors } = await createAccessGroup({
        variables: {
          input: {
            accessGroup: accessGroupPayload,
            accessGroupItem: accessGroupItems?.map((ele) => ({
              userGroupId: ele.userGroupId || '',
            })),
          },
        },
        update: (cache, { data }) => {
          if (!!data?.accessGroupCreate?.accessGroup?.id) {
            cache.modify({
              fields: {
                accessGroups(existing) {
                  if (data?.accessGroupCreate?.accessGroup) {
                    const newAccessGroupRef = cache.writeFragment({
                      data: data.accessGroupCreate.accessGroup,
                      fragment: ACCESS_GROUP_FIELDS,
                      fragmentName: ACCESS_GROUP_FRAGMENT_NAME,
                    });
                    return {
                      ...existing,
                      nodes: [newAccessGroupRef, ...existing.nodes],
                    };
                  }
                },
              },
            });
          }
        },
      });
      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        showCustomToast({
          content: 'User access pool created successfully',
          appearance: 'success',
        });
        onClose();
      }
    }
  };

  useEffect(() => {
    if (!accessGroup) reset(ACCESS_GROUP_INITIAL_VALUES);
    else {
      const defaultValues = getDefaultValues({ accessGroup });
      reset(defaultValues);
    }
    trigger();
  }, [accessGroup, isOpen, reset, trigger]);

  const loading = createLoading || updateLoading;

  return (
    <Modal isOpen={isOpen} isBlocking dragOptions={DragOptions}>
      <Stack className={styles.container}>
        {/* START OF FORM PROVIDER */}
        <FormProvider {...formMethods}>
          {/* HEADER */}
          <Header isEdit={!!accessGroup} onClose={onClose} />
          {/* FORM */}
          <BasicForm isUpdatable={!accessGroup || !!_isUpdatable} />
          <Separator />
          <GroupItems
            accessGroup={accessGroup}
            isUpdatable={!accessGroup || !!_isUpdatable}
          />
          <Separator />
          {/* FOOTER */}
          <Footer
            loading={loading}
            onSubmit={handleSubmit(onHandleSubmit)}
            onCancel={onClose}
          />
        </FormProvider>
        {/* END OF FORM PROVIDER */}
      </Stack>
    </Modal>
  );
};
